import React from 'react';
import styled from 'styled-components';
import { CornerRadius, Spacing } from 'styleguide/spacing';
import { View } from 'styleguide/View';
import { MuscleHeatmap } from 'components/MuscleHeatmap/MuscleHeatmap';
import { P1, P2, P3 } from 'styleguide/Texts';
import { MuscleGroup, MuscleHeatmapData } from 'hevy-shared';
import { FlexRow } from 'styleguide/Row';
import { colors } from 'styleguide/colors';
import { muscleGroupToHuman } from 'exercises/utils';
import { t } from 'translations/t';

const StatRow = styled(View)`
  align-items: flex-start;
`;

const HeatmapRow = styled(View)`
  flex-direction: row;
  gap: ${Spacing.sm}px;
  justify-content: center;
`;

const HeatmapColumn = styled(View)`
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const HeatmapMessage = styled(P3)`
  text-align: center;
`;

const BarChart = styled(View)`
  gap: ${Spacing.sm}px;
`;

const BarRow = styled(FlexRow)`
  gap: ${Spacing.sm}px;
`;

const BarTrack = styled.div`
  flex: 1;
  height: 20px;
  background-color: ${props => props.theme.secondary};
  overflow: hidden;
  border-radius: ${CornerRadius.xs}px;
`;

const BarFill = styled.div<{ widthPercent: number }>`
  height: 100%;
  width: ${props => props.widthPercent}%;
  background-color: ${colors.primary};
  border-radius: ${CornerRadius.xs}px;
`;

interface RoutineSummaryContentProps {
  data: MuscleHeatmapData;
  exerciseCount: number;
  setCount: number;
  estimatedDuration: string;
  setCountPerMuscleGroup: Record<MuscleGroup, number>;
  heatmapHeight?: number;
  heatmapWidth?: number;
}

export const RoutineSummaryContent = ({
  data,
  exerciseCount,
  setCount,
  estimatedDuration,
  setCountPerMuscleGroup,
  heatmapHeight = 200,
  heatmapWidth = 100,
}: RoutineSummaryContentProps) => {
  const entries = Object.entries(setCountPerMuscleGroup) as [MuscleGroup, number][];
  const maxSetCount = Math.max(...entries.map(([, count]) => count), 0);
  const muscleBars = entries
    .map(([muscleGroup, setCount]) => ({
      key: muscleGroup,
      label: muscleGroupToHuman(muscleGroup),
      value: setCount,
      widthPercent: maxSetCount > 0 ? (setCount / maxSetCount) * 100 : 0,
    }))
    .sort((a, b) => b.value - a.value);

  return (
    <View style={{ gap: Spacing.md }}>
      <FlexRow style={{ gap: Spacing.md }}>
        <StatRow>
          <P3 type="secondary">{t('global.exercises')}</P3>
          <P1>{exerciseCount}</P1>
        </StatRow>
        <StatRow>
          <P3 type="secondary">{t('web.routineSummary.totalSets')}</P3>
          <P1>{setCount}</P1>
        </StatRow>
        <StatRow>
          <P3 type="secondary">{t('web.routineSummary.estDuration')}</P3>
          <P1>{estimatedDuration}</P1>
        </StatRow>
      </FlexRow>
      <HeatmapRow>
        <HeatmapColumn>
          <MuscleHeatmap data={data} side="front" height={heatmapHeight} width={heatmapWidth} />
        </HeatmapColumn>
        <HeatmapColumn>
          <MuscleHeatmap data={data} side="back" height={heatmapHeight} width={heatmapWidth} />
        </HeatmapColumn>
      </HeatmapRow>
      {(exerciseCount === 0 || setCount === 0) && (
        <HeatmapMessage type="secondary">{t('web.routineSummary.addExerciseToVis')}</HeatmapMessage>
      )}
      {exerciseCount > 0 && setCount > 0 && (
        <BarChart>
          <BarRow style={{ justifyContent: 'space-between' }}>
            <P2 type="secondary">{t('web.routineSummary.muscle')}</P2>
            <P2 type="secondary">{t('web.routineSummary.sets')}</P2>
          </BarRow>
          {muscleBars
            .filter(bar => bar.value > 0)
            .map(({ key, label, value, widthPercent }) => (
              <BarRow key={key}>
                <P2 style={{ width: 80 }}>{label}</P2>
                <BarTrack>
                  <BarFill widthPercent={widthPercent} />
                </BarTrack>
                <P3 type="secondary" style={{ width: 32, textAlign: 'right' }}>
                  {Number.isInteger(value) ? value : value.toFixed(1)}
                </P3>
              </BarRow>
            ))}
        </BarChart>
      )}
    </View>
  );
};
